import type { ReactElement, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Placement } from '@popperjs/core';
import { createPopper } from '@popperjs/core';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

const DropdownPaper = styled.div<{
  $open: boolean;
  $width?: string;
  $zIndex?: number;
}>`
  position: absolute;
  z-index: ${({ theme, $zIndex }) => $zIndex ?? theme.zIndex.sidebar};
  min-width: 130px;
  width: ${({ $width }) => $width ?? 'auto'};
  max-width: min(320px, calc(100vw - 16px));
  padding: 0;
  margin: 0;
  border-radius: ${({ theme }) => theme.borderRadius}px;
  background: ${({ theme }) => theme.colors.white};
  box-shadow: ${({ theme }) => theme.shadows[1]};
  visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
  outline: none;

  ${({ theme }) => theme.device.mobile} {
    max-width: calc(100vw - 12px);
  }
`;

export interface DropdownProps {
  trigger: ReactElement;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  placement?: Placement;
  width?: string;
  /** z-index панели (портал в body). Для выпадашек внутри Drawer выше overlay (modal). */
  menuZIndex?: number;
  children: ReactNode;
}

export function Dropdown({
  trigger,
  open: controlledOpen,
  onOpenChange,
  placement = 'bottom-start',
  width,
  menuZIndex,
  children,
}: DropdownProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;

  const triggerRef = useRef<HTMLDivElement>(null);
  const paperRef = useRef<HTMLDivElement>(null);
  const popperRef = useRef<ReturnType<typeof createPopper> | null>(null);

  const setOpen = useCallback(
    (value: boolean) => {
      if (!isControlled) setInternalOpen(value);
      onOpenChange?.(value);
    },
    [isControlled, onOpenChange],
  );

  const handleTriggerClick = useCallback(() => {
    setOpen(!open);
  }, [open, setOpen]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const handleOutsideClick = useCallback(
    (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !open ||
        !triggerRef.current ||
        !paperRef.current ||
        !document.documentElement.contains(target)
      )
        return;
      if (
        !triggerRef.current.contains(target) &&
        !paperRef.current.contains(target)
      ) {
        handleClose();
      }
    },
    [open, handleClose],
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [handleOutsideClick]);

  useEffect(() => {
    if (open && triggerRef.current && paperRef.current) {
      popperRef.current = createPopper(
        triggerRef.current,
        paperRef.current,
        {
          placement,
          modifiers: [
            { name: 'offset', options: { offset: [0, 4] } },
            { name: 'preventOverflow', options: { padding: 8 } },
          ],
        },
      );
    }
    return () => {
      if (popperRef.current) {
        popperRef.current.destroy();
        popperRef.current = null;
      }
    };
  }, [open, placement]);

  const triggerWithRef = (
    <div ref={triggerRef} onClick={handleTriggerClick} style={{ display: 'inline-flex' }}>
      {trigger}
    </div>
  );

  const paper = (
    <DropdownPaper
      ref={paperRef}
      $open={open}
      $width={width}
      $zIndex={menuZIndex}
      role="menu"
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </DropdownPaper>
  );

  return (
    <>
      {triggerWithRef}
      {open && createPortal(paper, document.body)}
    </>
  );
}
